/**
 * Locale identity + detection/persistence. The portal ships English and
 * Brazilian Portuguese; the choice is a pure client concern (all server data is
 * locale-independent) persisted in localStorage. Detection order: stored
 * choice → browser language (any `pt-*` maps to pt-BR) → English.
 */

export type Locale = 'en' | 'pt-BR'

export const LOCALES: readonly Locale[] = ['en', 'pt-BR']

export const LOCALE_STORAGE_KEY = 'smith-portal.locale'

export function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'pt-BR'
}

/** The reader's own stored choice, or null when they never made one (a page may then pick for them). */
export function storedLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    return isLocale(stored) ? stored : null
  } catch {
    return null
  }
}

export function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (isLocale(stored)) return stored
  } catch {
    // Storage unavailable (private mode / disabled) — fall through to sniffing.
  }
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const candidate of candidates) {
    const lower = (candidate ?? '').toLowerCase()
    if (lower.startsWith('pt')) return 'pt-BR'
    if (lower.startsWith('en')) return 'en'
  }
  return 'en'
}

export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Best-effort: the in-memory choice still applies for this tab.
  }
}
